import React from 'react';
import { Edit, Power, PowerOff, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { Presentation } from '../types/system-parameters.types';

interface PresentationsTableProps {
  presentations: Presentation[];
  onEdit: (presentation: Presentation) => void;
  onDelete: (presentation: Presentation) => void;
}

type SortField = 'name';

export const PresentationsTable: React.FC<PresentationsTableProps> = ({
  presentations,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    name: 'Presentación'
  };

  const sortValueExtractor = (presentation: Presentation, field: SortField): any => {
    switch (field) {
      case 'name':
        return presentation.additionalData.name.toLowerCase();
      default:
        return presentation.additionalData.name;
    }
  };

  const tableData = useTableData<Presentation, SortField>(
    {
      data: presentations,
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (presentations.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay presentaciones</h3>
        <p className="text-gray-500">No se encontraron presentaciones con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Presentación
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((presentation) => (
              <tr key={presentation.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-gray-500" />
                      <div className="text-sm font-medium text-gray-900">
                        {presentation.additionalData.name}
                      </div>
                    </div>
                    {presentation.additionalData.description && (
                      <div className="text-xs text-gray-500 mt-1 ml-6">
                        {presentation.additionalData.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1 ml-6">
                      ID: {presentation.id}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge className={presentation.additionalData.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {presentation.additionalData.isActive ? 'Activa' : 'Inactiva'}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(presentation)}
                      className="text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(presentation)}
                      className={`text-xs ${presentation.additionalData.isActive
                        ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                        : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                    >
                      {presentation.additionalData.isActive ? (
                        <>
                          <PowerOff className="w-3 h-3 mr-1" />
                          Desactivar
                        </>
                      ) : (
                        <>
                          <Power className="w-3 h-3 mr-1" />
                          Reactivar
                        </>
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
